import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import {
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { publicFormApi } from '../api/diagnostics';
import { FormRenderer } from '../components/diagnostics/FormRenderer';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import type { FormStructure } from '@affogato/shared';

type PageState = 'loading' | 'password' | 'intro' | 'form' | 'submitted' | 'error';

interface FormInfo {
  title: string;
  description: string | null;
}

export function PublicFormPage() {
  const { t } = useTranslation('diagnostics');
  const { token } = useParams<{ token: string }>();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [formInfo, setFormInfo] = useState<FormInfo | null>(null);
  const [structure, setStructure] = useState<FormStructure | null>(null);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [requiresPassword, setRequiresPassword] = useState(false);

  // Password state
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // Respondent info
  const [respondentName, setRespondentName] = useState('');
  const [respondentEmail, setRespondentEmail] = useState('');
  const [respondentRole, setRespondentRole] = useState('');

  // Form state
  const [currentSection, setCurrentSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-save state
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Scroll to top when section changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSection]);

  useEffect(() => {
    if (token) {
      loadFormInfo();
    }
  }, [token]);

  const loadFormInfo = async () => {
    if (!token) return;

    try {
      const info = await publicFormApi.getInfo(token);

      if (!info.isActive) {
        setError(t('public.formNotAvailable'));
        setPageState('error');
        return;
      }

      setFormInfo({ title: info.title, description: info.description });
      setRequiresPassword(info.hasPassword);
      setPageState(info.hasPassword ? 'password' : 'intro');
    } catch (err) {
      console.error('Error loading form info:', err);
      setError(t('public.formNotAvailable'));
      setPageState('error');
    }
  };

  const handleVerifyPassword = async () => {
    if (!token || !password) return;

    setIsVerifying(true);
    setPasswordError(false);

    try {
      const result = await publicFormApi.verifyPassword(token, password);
      if (result.valid) {
        setPageState('intro');
      } else {
        setPasswordError(true);
      }
    } catch {
      setPasswordError(true);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleStartForm = async () => {
    if (!token) return;

    setPageState('loading');
    try {
      // Load full form structure
      const formData = await publicFormApi.getForm(token, requiresPassword ? password : undefined);
      setStructure(formData.structure);

      // Start response
      const response = await publicFormApi.startResponse(token, {
        respondentName: respondentName || undefined,
        respondentEmail: respondentEmail || undefined,
        respondentRole: respondentRole || undefined,
      });
      setResponseId(response.responseId);
      setFormInfo({ title: formData.title, description: formData.description });
      setPageState('form');
    } catch (err) {
      console.error('Error starting form:', err);
      setError(t('errors.loadFailed'));
      setPageState('error');
    }
  };

  const handleAnswerChange = async (questionId: string, value: unknown) => {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);

    // Auto-save progress with feedback
    if (token && responseId) {
      setSaveStatus('saving');
      try {
        await publicFormApi.updateResponse(token, responseId, { answers: newAnswers });
        setSaveStatus('saved');

        // Reset to idle after 2 seconds
        setTimeout(() => {
          setSaveStatus((current) => (current === 'saved' ? 'idle' : current));
        }, 2000);
      } catch (err) {
        console.error('Error saving progress:', err);
        setSaveStatus('error');

        // Retry after 5 seconds
        setTimeout(async () => {
          if (token && responseId) {
            try {
              await publicFormApi.updateResponse(token, responseId, { answers: newAnswers });
              setSaveStatus('saved');
            } catch {
              // Keep error state
            }
          }
        }, 5000);
      }
    }
  };

  const handleSubmit = async () => {
    if (!token || !responseId) return;

    setIsSubmitting(true);
    try {
      await publicFormApi.submitResponse(token, responseId);
      setPageState('submitted');
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(t('errors.submitFailed') || 'Error submitting form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSections = structure?.sections.length || 0;
  const progress = totalSections > 0 ? ((currentSection + 1) / totalSections) * 100 : 0;

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 text-center"
        >
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t('public.formNotAvailable')}
          </h1>
          <p className="text-gray-500 dark:text-slate-400">{error}</p>
        </motion.div>
      </div>
    );
  }

  // Password state
  if (pageState === 'password') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8"
        >
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <LockClosedIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
            {formInfo?.title}
          </h1>
          <p className="text-gray-500 dark:text-slate-400 text-center mb-6">
            {t('public.enterPassword')}
          </p>

          <div className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError(false);
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleVerifyPassword()}
                placeholder={t('public.password')}
                className={`w-full px-4 py-3 bg-white dark:bg-slate-700 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  passwordError
                    ? 'border-red-500'
                    : 'border-gray-200 dark:border-slate-600'
                }`}
              />
              {passwordError && (
                <p className="text-sm text-red-500 mt-1">
                  {t('public.incorrectPassword')}
                </p>
              )}
            </div>
            <Button
              onClick={handleVerifyPassword}
              disabled={!password || isVerifying}
              className="w-full"
            >
              {isVerifying ? <Spinner size="sm" /> : t('public.access')}
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  // Intro state
  if (pageState === 'intro') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8"
        >
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <ClipboardDocumentListIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
            {t('public.welcome')}
          </h1>
          <h2 className="text-lg text-gray-700 dark:text-slate-300 text-center mb-2">
            {formInfo?.title}
          </h2>
          {formInfo?.description && (
            <p className="text-gray-500 dark:text-slate-400 text-center mb-6">
              {formInfo.description}
            </p>
          )}

          <div className="space-y-4 mb-6">
            <p className="text-sm font-medium text-gray-700 dark:text-slate-300">
              {t('public.respondentInfo')}
            </p>
            <input
              type="text"
              value={respondentName}
              onChange={(e) => setRespondentName(e.target.value)}
              placeholder={t('public.name')}
              className="w-full px-4 py-3 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <input
              type="email"
              value={respondentEmail}
              onChange={(e) => setRespondentEmail(e.target.value)}
              placeholder={t('public.email')}
              className="w-full px-4 py-3 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <input
              type="text"
              value={respondentRole}
              onChange={(e) => setRespondentRole(e.target.value)}
              placeholder={t('public.role')}
              className="w-full px-4 py-3 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <Button onClick={handleStartForm} className="w-full">
            {t('public.startForm')}
          </Button>
        </motion.div>
      </div>
    );
  }

  // Submitted state
  if (pageState === 'submitted') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 text-center"
        >
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircleIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {t('public.submitted')}
          </h1>
          <p className="text-gray-500 dark:text-slate-400">
            {t('public.thankYou')}
          </p>
        </motion.div>
      </div>
    );
  }

  // Form state
  if (pageState === 'form' && structure) {
    const currentSectionData = structure.sections[currentSection];
    const isLastSection = currentSection === totalSections - 1;
    const isFirstSection = currentSection === 0;

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        {/* Progress bar */}
        {structure.settings.showProgressBar && (
          <div className="fixed top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-slate-700 z-50">
            <motion.div
              className="h-full bg-primary-500"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        )}

        {/* Header */}
        <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-40">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-lg font-medium text-gray-900 dark:text-white">
                  {formInfo?.title}
                </h1>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {t('public.section')} {currentSection + 1} {t('public.of')} {totalSections}
                </p>
              </div>
              {/* Auto-save status indicator */}
              <div className="text-xs">
                {saveStatus === 'saving' && (
                  <span className="flex items-center text-gray-500 dark:text-slate-400">
                    <Spinner size="sm" className="mr-1" />
                    {t('public.saving') || 'Guardando...'}
                  </span>
                )}
                {saveStatus === 'saved' && (
                  <span className="flex items-center text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                    {t('public.saved') || 'Guardado'}
                  </span>
                )}
                {saveStatus === 'error' && (
                  <span className="flex items-center text-red-600 dark:text-red-400">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                    {t('public.saveError') || 'Error al guardar'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="max-w-3xl mx-auto px-4 py-8 pb-24">
          <FormRenderer
            section={currentSectionData}
            answers={answers}
            onAnswerChange={handleAnswerChange}
            showSectionTitle={structure.settings.showSectionTitles}
          />
        </div>

        {/* Navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700">
          <div className="max-w-3xl mx-auto px-4 py-4 flex justify-between">
            <Button
              variant="secondary"
              onClick={() => setCurrentSection((s) => s - 1)}
              disabled={isFirstSection}
            >
              {t('public.previous')}
            </Button>

            {isLastSection ? (
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? <Spinner size="sm" /> : t('public.submit')}
              </Button>
            ) : (
              <Button onClick={() => setCurrentSection((s) => s + 1)}>
                {t('public.next')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}
